'use strict';

import { AGENT_STATE } from '@/constants/agent/constants';
import LoginService from '@/services/loginService';
import AgentService from '@/services/agentService';
import ThemeUtility from '@/components/ecs-theme/ecs-theme-utility';
import { STATUS_SUCCESS } from '@/constants/appConstants';
import HttpRequestHandler from '@/plugins/http';
import OpenIdLoginService from '@/services/openIdLoginService';

const socketEventActions = {
  SOCKET_EVENT_AGENT_STATE_CHANGED_EVENT({ commit, dispatch, getters }, event) {
    if (event.agentId === getters.loggedInAgent.id) {
      dispatch('updateLoggedInAgentState', event);
    }
  },

  SOCKET_EVENT_AGENT_LOAD_ALLOWANCE_CHANGED_EVENT({ commit, getters }, event) {
    const { agentId, loadAllowanceId, loadAllowanceName } = event;
    if (agentId === getters.loggedInAgent.id) {
      commit('updateLoggedInAgentLoadAllowance', { loadAllowanceId, loadAllowanceName });
    }
  },

  SOCKET_EVENT_AGENT_TERMINAL_CHANGED_EVENT({ dispatch, getters }, { terminal, phoneType }) {
    const agentTerminal = { terminalType: phoneType, terminalURI: terminal };

    if (!agentTerminal.terminalURI && agentTerminal.terminalType) {
      const availableTerminal = getters.telephoneTerminals.find(t => t.terminalType === agentTerminal.terminalType);
      if (availableTerminal) {
        agentTerminal.terminalURI = availableTerminal.uri;
      }
    }

    dispatch('setAgentCurrentExtension', agentTerminal);
  }
};

const stateActions = {
  async loginAgent({ dispatch, commit, rootGetters }, options) {
    const loginService = options.code ? OpenIdLoginService.login : LoginService.login;
    const response = await loginService(options).catch(() => {
      throw new Error('Failed to login');
    });

    if (response.status === STATUS_SUCCESS && response.data) {
      const {
        agentInformation,
        sessionId,
        agentSettings,
        softPhoneSettings,
        telephoneTerminals,
        organizationData,
        systemData
      } = response.data;

      // Store a flag in local storage to indicate if running on MS Teams
      if (options.teams) {
        localStorage.setItem('runningOnTeams', 'true');
      } else {
        localStorage.removeItem('runningOnTeams');
      }

      // Store sessionId in local storage if running on MS Teams
      if (options.teams) {
        localStorage.setItem('sessionId', sessionId);
      }
      sessionStorage.setItem('sessionId', sessionId);

      // Store current agent info
      if (options.teams) {
        localStorage.setItem('agent', JSON.stringify(agentInformation));
      }
      sessionStorage.setItem('agent', JSON.stringify(agentInformation));

      await dispatch('setLoggedInAgent', agentInformation);
      HttpRequestHandler.setSessionUsername(agentInformation.username);

      // Store current agent organization data.
      organizationData.shortName = agentInformation.org || organizationData.orgAbbreviation;

      if (options.teams) {
        localStorage.setItem('organizationData', JSON.stringify(organizationData));
      }
      sessionStorage.setItem('organizationData', JSON.stringify(organizationData));

      dispatch('ORGANIZATION/setOrganizationData', organizationData, { root: true });
      dispatch('ORGANIZATION/setOrganizationShortName', organizationData.shortName, { root: true });

      if (!agentInformation.isWallboard) {
        dispatch('REFERENCES/setSmtpDetails', organizationData.smtp || {}, { root: true });
        dispatch('REFERENCES/setSmsDetails', organizationData.sms || {}, { root: true });
      }

      dispatch('REFERENCES/setAvailableTimeZones', organizationData.availableTimeZones, { root: true });

      // Store current enterprise-org system data.
      if (options.teams) {
        localStorage.setItem('systemData', JSON.stringify(systemData));
      }
      sessionStorage.setItem('systemData', JSON.stringify(systemData));
      dispatch('SYSTEM/setSystemData', systemData, { root: true });

      // Store soft phone settings only if not running on MS Teams
      if (!options.teams) {
        sessionStorage.setItem('softPhoneSettings', JSON.stringify(softPhoneSettings));
        dispatch('SETTINGS/setSoftPhoneSettings', softPhoneSettings, { root: true });
      }

      commit('setTelephoneTerminals', telephoneTerminals);

      if (!agentInformation.isWallboard) {
        if (Object.keys(agentSettings).length > 1) {
          // Special server side field to assist with time zone change
          if (agentSettings.timeZoneFormatChanged) {
            const defaultTimeZone = rootGetters['REFERENCES/defaultTimeZone'];
            const updatedAgentSettings = { ...agentSettings };
            updatedAgentSettings.applicationSettings.timeZoneId = defaultTimeZone.value;
            updatedAgentSettings.applicationSettings.timeZoneUtcOffset = defaultTimeZone.utc;

            dispatch('SETTINGS/updateCurrentUserSettings', updatedAgentSettings, { root: true });
          }

          if (agentInformation.isSupervisor) {
            if (
              !agentSettings.supervisorWallboardSettings ||
              !agentSettings.supervisorWallboardSettings.wallboards ||
              agentSettings.supervisorWallboardSettings.wallboards.length < 1
            ) {
              agentSettings.supervisorWallboardSettings = {
                selectedWallboardIndex: -1,
                wallboards: [{ name: 'Default Wallboard' }]
              };
            }
            dispatch('SETTINGS/updateCurrentSupervisorWallboardSettings', agentSettings.supervisorWallboardSettings, {
              root: true
            });
          }
          dispatch('SETTINGS/setCurrentUserSettings', agentSettings, { root: true });
        } else {
          const newSettings = await dispatch('SETTINGS/createCurrentUserSettings', null, { root: true });
          if (newSettings) {
            ThemeUtility.build(newSettings.theme);
          }
          response.data.agentSettings = newSettings;
        }
      }
    }

    return response.data;
  },

  retrieveWallboardUser({ dispatch }) {
    const agentSessionData = JSON.parse(sessionStorage.getItem('agent'));
    HttpRequestHandler.setSessionUsername(agentSessionData.account);

    dispatch('setLoggedInAgent', agentSessionData);

    return agentSessionData;
  },

  retrieveLoggedInAgentData({ dispatch }) {
    const agentSessionData = JSON.parse(sessionStorage.getItem('agent'));
    const organizationData = JSON.parse(sessionStorage.getItem('organizationData'));
    HttpRequestHandler.setSessionUsername(agentSessionData.account);

    const orgName = organizationData.shortName;
    const orgId = organizationData.orgId;

    return AgentService.getLoggedInAgentData({ orgName, orgId }).then(({ data }) => {
      // PATCH: ECS doesn't provide the isSupervisor and pbxEnterpriseID info
      // so it's being retrieved from sessionStorage that was stored when login first time :(
      data.isSupervisor = agentSessionData ? agentSessionData.isSupervisor : false;
      data.pbxEnterpriseID = agentSessionData ? agentSessionData.pbxEnterpriseID : null;
      data.organizationData = organizationData || {};

      if (data.configurationVersion !== organizationData.configurationVersion) {
        dispatch('ORGANIZATION/updateOrgConfiguration', data.configurationVersion, { root: true });
      }

      if (organizationData.shortName) {
        dispatch('ORGANIZATION/setOrganizationShortName', organizationData.shortName, { root: true });
      }

      dispatch('setLoggedInAgent', data);
      return data;
    });
  },

  updateLoggedInAgentState({ commit, dispatch, getters }, agentState) {
    commit('updateLoggedInAgentState', agentState);

    if (agentState.stateId === AGENT_STATE.offline) {
      // PATCH: ECS doesn't provide the event to update the load allowance to the default one
      // when state is changed to offline :(
      const defaultLoadAllowance = getters.loggedInAgent.availableLoadAllowances.find(
        item => item.name === getters.loggedInAgent.defaultLoadAllowance
      );
      if (defaultLoadAllowance) {
        const { id: loadAllowanceId, name: loadAllowanceName } = defaultLoadAllowance;
        commit('updateLoggedInAgentLoadAllowance', { loadAllowanceId, loadAllowanceName });
      }
    }
  },

  updateLoggedInAgentStateIfNeeded({ commit, dispatch, getters }, agentState) {
    if (agentState.stateId !== getters.agentState.stateId) {
      commit('updateLoggedInAgentState', agentState);
    }
  },

  updateSoftPhoneRegisteredState({ commit }, isRegistered) {
    commit('updateSoftPhoneRegisteredState', isRegistered);
  },

  updateIsRegisteringSoftPhone({ commit }, isRegistering) {
    commit('updateIsRegisteringSoftPhone', isRegistering);
  },

  updateSoftPhoneRegistrationErrors({ commit }, errors) {
    commit('updateSoftPhoneRegistrationErrors', errors);
  },

  updateLoggedInAgentLoadAllowance({ commit }, data) {
    const { loadAllowanceId, loadAllowanceName } = data;
    return AgentService.updateLoggedInAgentLoadAllowance(loadAllowanceId).then(() => {
      commit('updateLoggedInAgentLoadAllowance', {
        loadAllowanceId,
        loadAllowanceName
      });
    });
  },

  setRegistrationId({ commit }, registrationId) {
    commit('setRegistrationId', registrationId);
  },

  setLoggedInAgent({ commit }, agent) {
    if (agent) {
      commit('setLoggedInAgent', agent);
    }
  },

  removeLoggedInAgent({ commit }) {
    commit('removeLoggedInAgent');
    commit('setRegistrationId', null);
    commit('setTelephoneTerminals', []);
    sessionStorage.clear();
    localStorage.removeItem('sessionId');
    localStorage.removeItem('agent');
    localStorage.removeItem('organizationData');
    localStorage.removeItem('systemData');
    localStorage.removeItem('runningOnTeams');
    localStorage.removeItem('offlineWarningDisabled');
  },

  setAgentCurrentExtension({ commit }, payload) {
    commit('setAgentCurrentExtension', payload);
  },

  async updateTelephoneTerminals({ commit, getters }, { callback }) {
    const response = await AgentService.getAvailableTerminals(getters.loggedInAgent.id);
    commit('setTelephoneTerminals', response.data || []);
    callback && callback();
  },

  retrieveAgentCallbacks() {
    // TODO - Move EcsCallbacksPanel logic to sort and create notifications to this action.
    return AgentService.getAgentCallbacks().then(response => response.data);
  },

  updateAgentAssignedBusinessProcessIds({ commit }, payload) {
    commit('setAgentAssignedBusinessProcessIds', payload);
  }
};

export default {
  ...socketEventActions,
  ...stateActions
};
